import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';

export class ExportUtils {
    /**
     * Export data to Excel
     */
    static exportToExcel(data, filename = 'export') {
        try {
            if (!data || data.length === 0) {
                throw new Error('No data to export');
            }

            // Prepare data for Excel
            const excelData = data.map(item => ({
                'Source': item.source_type ? item.source_type.toUpperCase() : 'Unknown',
                'Receipt No.': item.receipt_no || '',
                'Date & Time': item.completion_time ? format(new Date(item.completion_time), 'dd/MM/yyyy HH:mm:ss') : '',
                'Details': item.details || '',
                'Type': item.transaction_type || 'Unknown',
                'Amount': item.paid_in > 0 ? `+${parseFloat(item.paid_in).toFixed(2)}` :
                    item.withdrawn > 0 ? `-${parseFloat(item.withdrawn).toFixed(2)}` : '0.00',
                'Balance': parseFloat(item.balance || 0).toFixed(2),
                'Remarks': item.remarks || '',
                'Status': item.transaction_status || 'Completed'
            }));

            const ws = XLSX.utils.json_to_sheet(excelData);

            // Set column widths
            ws['!cols'] = [
                { wch: 12 }, // Source
                { wch: 15 }, // Receipt No.
                { wch: 20 }, // Date & Time
                { wch: 40 }, // Details
                { wch: 12 }, // Type
                { wch: 15 }, // Amount
                { wch: 15 }, // Balance
                { wch: 20 }, // Remarks
                { wch: 12 }, // Status
            ];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Transactions');

            // Add summary sheet
            const totalCredit = data.reduce((sum, item) => sum + (parseFloat(item.paid_in) || 0), 0);
            const totalDebit = data.reduce((sum, item) => sum + (parseFloat(item.withdrawn) || 0), 0);
            const mpesaCount = data.filter(item => item.source_type === 'mpesa').length;
            const bankCount = data.filter(item => item.source_type === 'bank').length;

            const summaryData = [
                ['Transaction Summary'],
                [''],
                ['Total Transactions', data.length],
                ['Total Credits', totalCredit.toFixed(2)],
                ['Total Debits', totalDebit.toFixed(2)],
                ['Net Balance', (totalCredit - totalDebit).toFixed(2)],
                [''],
                ['Source Breakdown'],
                ['M-Pesa', mpesaCount],
                ['Bank', bankCount],
                [''],
                ['Generated:', format(new Date(), 'dd/MM/yyyy HH:mm:ss')]
            ];

            const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
            XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

            // Generate Excel file
            const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wbout], { type: 'application/octet-stream' });
            saveAs(blob, `${filename}.xlsx`);

            return true;
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            throw new Error(`Excel export failed: ${error.message}`);
        }
    }

    /**
     * Export data to PDF
     */
    static exportToPDF(data, title = 'Transaction Report') {
        try {
            if (!data || data.length === 0) {
                throw new Error('No data to export');
            }

            const doc = new jsPDF('landscape', 'mm', 'a4');

            // Header
            doc.setFontSize(18);
            doc.setTextColor(33, 33, 33);
            doc.text(title, 14, 20);

            // Date
            doc.setFontSize(10);
            doc.setTextColor(100, 100, 100);
            doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}`, 14, 28);

            // Summary
            const totalCredit = data.reduce((sum, item) => sum + (parseFloat(item.paid_in) || 0), 0);
            const totalDebit = data.reduce((sum, item) => sum + (parseFloat(item.withdrawn) || 0), 0);

            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0);
            const summaryText = [
                `Total Transactions: ${data.length}`,
                `Total Credits: KES ${totalCredit.toFixed(2)}`,
                `Total Debits: KES ${totalDebit.toFixed(2)}`,
                `Net Balance: KES ${(totalCredit - totalDebit).toFixed(2)}`
            ];
            doc.text(summaryText, 14, 36);

            // Table
            const tableData = data.map(item => [
                item.source_type ? item.source_type.toUpperCase() : 'Unknown',
                item.receipt_no || '',
                item.completion_time ? format(new Date(item.completion_time), 'dd/MM/yyyy HH:mm') : '',
                (item.details || '').substring(0, 30),
                item.transaction_type || 'Unknown',
                item.paid_in > 0 ? `+${parseFloat(item.paid_in).toFixed(2)}` :
                    item.withdrawn > 0 ? `-${parseFloat(item.withdrawn).toFixed(2)}` : '0.00',
                parseFloat(item.balance || 0).toFixed(2)
            ]);

            autoTable(doc, {
                head: [['Source', 'Receipt No.', 'Date', 'Details', 'Type', 'Amount', 'Balance']],
                body: tableData,
                startY: 44,
                theme: 'grid',
                styles: {
                    fontSize: 7,
                    cellPadding: 2,
                },
                headStyles: {
                    fillColor: [33, 150, 243],
                    textColor: [255, 255, 255],
                    fontSize: 8,
                    fontStyle: 'bold',
                },
                alternateRowStyles: {
                    fillColor: [240, 245, 255],
                },
                columnStyles: {
                    0: { cellWidth: 20 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 30 },
                    3: { cellWidth: 45 },
                    4: { cellWidth: 20 },
                    5: { cellWidth: 25 },
                    6: { cellWidth: 25 },
                },
                didDrawPage: function (data) {
                    doc.setFontSize(8);
                    doc.setTextColor(150, 150, 150);
                    doc.text(
                        `Page ${data.pageNumber}`,
                        doc.internal.pageSize.width - 20,
                        doc.internal.pageSize.height - 10
                    );
                },
            });

            doc.save(`${title.replace(/\s+/g, '_')}.pdf`);
            return true;
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            throw new Error(`PDF export failed: ${error.message}`);
        }
    }

    /**
     * Export data to CSV
     */
    static exportToCSV(data, filename = 'export') {
        try {
            if (!data || data.length === 0) {
                throw new Error('No data to export');
            }

            const headers = ['Source', 'Receipt No.', 'Date', 'Details', 'Type', 'Amount', 'Balance', 'Status'];

            const rows = data.map(item => [
                item.source_type ? item.source_type.toUpperCase() : 'Unknown',
                `"${item.receipt_no || ''}"`,
                item.completion_time ? format(new Date(item.completion_time), 'dd/MM/yyyy HH:mm:ss') : '',
                `"${(item.details || '').replace(/"/g, '""')}"`,
                item.transaction_type || 'Unknown',
                item.paid_in > 0 ? `+${parseFloat(item.paid_in).toFixed(2)}` :
                    item.withdrawn > 0 ? `-${parseFloat(item.withdrawn).toFixed(2)}` : '0.00',
                parseFloat(item.balance || 0).toFixed(2),
                item.transaction_status || 'Completed'
            ]);

            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, `${filename}.csv`);

            return true;
        } catch (error) {
            console.error('Error exporting to CSV:', error);
            throw new Error(`CSV export failed: ${error.message}`);
        }
    }

    /**
     * Export summary to PDF
     */
    static exportSummaryToPDF(summary, data, title = 'Summary Report') {
        try {
            const doc = new jsPDF('portrait', 'mm', 'a4');

            doc.setFontSize(20);
            doc.setTextColor(33, 33, 33);
            doc.text(title, 14, 20);

            doc.setFontSize(10);
            doc.setTextColor(100, 100, 100);
            doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}`, 14, 28);

            const stats = summary?.summary || {};
            const cardData = [
                ['Total Transactions', stats.total_transactions || 0],
                ['Total Paid In', `KES ${(stats.total_paid_in || 0).toFixed(2)}`],
                ['Total Withdrawn', `KES ${(stats.total_withdrawn || 0).toFixed(2)}`],
                ['Net Balance', `KES ${((stats.total_paid_in || 0) - (stats.total_withdrawn || 0)).toFixed(2)}`],
                ['M-Pesa Transactions', stats.mpesa_count || 0],
                ['Bank Transactions', stats.bank_count || 0],
            ];

            let yPos = 38;
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0);

            cardData.forEach(([label, value]) => {
                doc.text(`${label}:`, 14, yPos);
                doc.setFont('helvetica', 'bold');
                doc.text(value.toString(), 80, yPos);
                doc.setFont('helvetica', 'normal');
                yPos += 8;
            });

            doc.save(`${title.replace(/\s+/g, '_')}.pdf`);
            return true;
        } catch (error) {
            console.error('Error exporting summary to PDF:', error);
            throw new Error(`Summary export failed: ${error.message}`);
        }
    }
}

export default ExportUtils;